import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the per-row team-mapping select and its select2, re-applied
// on every draw. Only the edit view (`properties?edit=true`) renders a select in the team column,
// so this is a no-op on the show view.
//
// DataTables fires "draw.dt" through jQuery's event bus rather than a native dispatch, so a
// data-action never sees it; bind with jQuery, namespaced, in connect()/disconnect().
export default class extends Controller {
  static get targets() {
    return ["table"];
  }

  connect() {
    this.onDraw = this.onDraw.bind(this);
    $(this.tableTarget).on("draw.dt.yardiProperties", this.onDraw);
    // The initial ajax-driven draw can land before or after this connect(), depending on which
    // of the two sibling controllers wins the mount race, so bind once here too in case a draw
    // already happened.
    this.bindSelects();
  }

  disconnect() {
    $(this.tableTarget).off(".yardiProperties");

    const $initialized = this.selects().filter(".select2-hidden-accessible");
    if ($initialized.length) {
      $initialized.select2("destroy");
    }
  }

  onDraw() {
    this.bindSelects();
  }

  // Returns a jQuery collection deliberately: it feeds select2 initialization directly below and
  // the destroy call in disconnect(), and select2 has no native-DOM equivalent to call instead.
  selects() {
    return $(this.tableTarget).find("select");
  }

  bindSelects() {
    const $selects = this.selects();

    $selects.off("change.yardiProperties").on("change.yardiProperties", (event) => {
      this.mapProperty($(event.target));
    });

    $selects.select2({
      placeholder: "Select Team",
      width: "100%",
      templateResult: (data) => data.text,
    });
  }

  // $select stays jQuery for the .data()/.val() reads and the select2 refresh dance in
  // onMapPropertySuccess below (see selects()). The selected option's own text reads through a
  // plain querySelector instead: jQuery's "option:selected" pseudo-selector throws a SyntaxError
  // under a native querySelector, so this uses "option:checked" -- the standard CSS equivalent.
  mapProperty($select) {
    const url = $select.data("url");
    const teamId = $select.val();
    const teamName = $select[0].querySelector("option:checked").textContent;

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({ team_id: teamId });

    fetch(url, options).
      then((response) => {
        if (!response.ok) {
          throw new Error(`map_property failed with status ${response.status}`);
        }

        this.onMapPropertySuccess($select, teamId, teamName);
      }).
      catch(this.logFailedCall);
  }

  onMapPropertySuccess($select, teamId, teamName) {
    const previousTeamId = $select.data("previousId");
    const previousTeamName = $select.data("previousName");
    window.ltAlert({ message: "Mapping updated.", type: "toast-success" });

    // otherSelects is the same select2-initialized jQuery collection selects() returns above.
    // Appending a plain <option> and then firing select2's own "change.select2" event is the
    // documented way to add an option to an already-initialized select2 widget and have it
    // re-render -- there is no native equivalent to select2's own refresh.
    const otherSelects = this.selects().not(`#${$select.attr("id")}`);
    if (previousTeamId != 0) {
      otherSelects.append($("<option>", { value: previousTeamId, text: previousTeamName }));
    }
    otherSelects.find(`option[value='${teamId}']`).remove();

    this.selects().trigger("change.select2");
    $select.data("previousId", teamId);
    $select.data("previousName", teamName);
  }

  logFailedCall(response) {
    console.warn(response);
    window.ltAlert({ message: "An error occurred, please try again later.", type: "toast-error" });
  }
}
